import { type NewCoreIconProps } from '@atlaskit/icon';
import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import CheckboxCheckedIcon from '@atlaskit/icon/core/checkbox-checked';
import NumberIcon from '@atlaskit/icon/core/hashtag';
import ImageIcon from '@atlaskit/icon/core/image';
import LinkIcon from '@atlaskit/icon/core/link';
import ListBulletedIcon from '@atlaskit/icon/core/list-bulleted';
import PageIcon from '@atlaskit/icon/core/page';
import PersonIcon from '@atlaskit/icon/core/person';
import NewTagIcon from '@atlaskit/icon/core/tag';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
import BookOpenIcon from '@atlaskit/icon-lab/core/book-open';
import LozengeIcon from '@atlaskit/icon-lab/core/lozenge';
import Tooltip from '@atlaskit/tooltip';
import { DatabaseFieldType } from '@databases/types/database';
import React, { type ComponentType, type FunctionComponent } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl-next';

import { EntryOutlineIcon } from './icons';

export interface DatabaseFieldIconType {
  icon: ComponentType<NewCoreIconProps>;
}

export const FIELD_ICONS: { [Type in DatabaseFieldType]: DatabaseFieldIconType } = {
  [DatabaseFieldType.TEXT]: {
    icon: AlignTextLeftIcon,
  },
  [DatabaseFieldType.ATTACHMENT]: {
    icon: ImageIcon,
  },
  [DatabaseFieldType.NUMBER]: {
    icon: NumberIcon,
  },
  [DatabaseFieldType.DATE]: {
    icon: CalendarIcon,
  },
  [DatabaseFieldType.SELECT]: {
    icon: NewTagIcon,
  },
  [DatabaseFieldType.USER]: {
    icon: PersonIcon,
  },
  [DatabaseFieldType.HYPERLINK]: {
    icon: LinkIcon,
  },
  [DatabaseFieldType.CHECKBOX]: {
    icon: CheckboxCheckedIcon,
  },
  [DatabaseFieldType.PAGE_LINK]: {
    icon: PageIcon,
  },
  [DatabaseFieldType.PAGE_STATUS]: {
    icon: CheckCircleIcon,
  },
  [DatabaseFieldType.PAGE_LABELS]: {
    icon: LozengeIcon,
  },
  [DatabaseFieldType.PAGE_DETAILS]: {
    icon: BookOpenIcon,
  },
  [DatabaseFieldType.ENTRY_LINK]: {
    icon: EntryOutlineIcon,
  },
  [DatabaseFieldType.ENTRY_DETAILS]: {
    icon: ListBulletedIcon,
  },
  [DatabaseFieldType.BACKLINK]: {
    icon: ArrowLeftIcon,
  },
  [DatabaseFieldType.JIRA_ISSUE]: {
    icon: WorkItemIcon,
  },
  [DatabaseFieldType.JIRA_ISSUE_DETAILS]: {
    icon: BookOpenIcon,
  },
};

export interface FieldTypeIconProps {
  type: DatabaseFieldType;
  label?: MessageDescriptor;
  noTooltip?: boolean;
}

/**
 * Renders an icon that indicates the specified field type. A tooltip text will describe the field type (unless disabled).
 */
export const FieldTypeIcon: FunctionComponent<FieldTypeIconProps> = ({
  type,
  label,
  noTooltip,
}) => {
  const { formatMessage } = useIntl();

  const iconData = FIELD_ICONS[type];
  const icon = <iconData.icon label="" testId={`field-type-icon-${type}`} />;

  if (noTooltip || !label) {
    return icon;
  }

  const formattedLabel = formatMessage(label);

  return (
    <Tooltip tag="span" content={formattedLabel}>
      {icon}
    </Tooltip>
  );
};
